import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { CalendarClock, Check } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { MultiSelect } from '@/components/ui/multi-select'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { AdvancedSection } from '../AdvancedSection'
import { FieldLabel } from '../FieldLabel'
import { TokenInput } from '../TokenInput'
import { CurrencySelect } from '../CurrencySelect'
import { useNetwork } from '../../contexts/NetworkContext'
import { useCurrentUser } from '../../contexts/UserContext'
import { UsersApi } from '@/services/users/usersApi'
import {
  MAX_DAY_OF_MONTH,
  WEEK_DAYS,
  cadence,
  cadenceOf,
  cadencesFor,
  type CadenceKey,
} from '@/services/reports/reportCadence'
import {
  ReportsApi,
  REPORT_TARGETS,
  newReportId,
  type ReportPeriod,
  type ReportSchedule,
  type ReportTarget,
  type Recurrence,
  type WeekDay,
} from '@/services/reports/reportsApi'

/** Coverage options, in the order they are worth considering. */
const PERIODS: ReportPeriod[] = [
  'RECURRENCE_PERIOD',
  'FLIGHT',
  'CURRENT_MONTH',
  'DAILY',
  'WEEKLY',
  'MONTHLY',
]

const RECURRENCES: Recurrence[] = ['DAILY', 'WEEKLY', 'MONTHLY', 'END_OF_FLIGHT', 'ONCE']

interface ScheduleReportDialogProps {
  target: ReportTarget
  /** The schedule being edited; omitted when a new one is being set up. */
  schedule?: ReportSchedule
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Sets up a standing report. The API takes a recurrence, a period, a day and a
 * time as four independent fields, most of whose combinations are meaningless
 * — so the dialog asks the question people actually have ("how often, covering
 * what?") as a handful of named cadences, and says in a sentence what each one
 * will send. Everything else is defaulted from the network and the user, and
 * only shown to someone who opens the advanced section.
 */
export function ScheduleReportDialog({
  target,
  schedule,
  open,
  onOpenChange,
}: ScheduleReportDialogProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()
  const currentUser = useCurrentUser()

  const [templateId, setTemplateId] = useState('')
  const [cadenceKey, setCadenceKey] = useState<CadenceKey | 'custom'>('dailyYesterday')
  const [recurrence, setRecurrence] = useState<Recurrence>('DAILY')
  const [period, setPeriod] = useState<ReportPeriod>('RECURRENCE_PERIOD')
  const [periodTime, setPeriodTime] = useState('07:00')
  const [dayOfWeek, setDayOfWeek] = useState<WeekDay>('MONDAY')
  const [dayOfMonth, setDayOfMonth] = useState(1)
  const [name, setName] = useState('')
  const [nameEdited, setNameEdited] = useState(false)
  const [userIds, setUserIds] = useState<string[]>([])
  const [emails, setEmails] = useState<string[]>([])
  const [currency, setCurrency] = useState('')
  const [advanced, setAdvanced] = useState(false)

  const { data: templates = [] } = useQuery({
    queryKey: ['report-templates', target.type],
    queryFn: () => ReportsApi.getTemplates(target.type),
    enabled: open,
  })
  const { data: users } = useQuery({
    queryKey: ['users', 'report-recipients'],
    queryFn: () => UsersApi.getAll({ minimal: true }),
    enabled: open,
  })

  const template = templates.find((item) => item.id === templateId)
  const offered = cadencesFor(template?.reportType)
  const timeZone = schedule?.timeZone || currentNetwork?.timeZone || 'UTC'

  // Fill the form from the schedule being edited, or from the defaults, each
  // time the dialog is opened — never while it is open, so nothing the person
  // typed is pulled out from under them.
  useEffect(() => {
    if (!open) return
    const matched = schedule ? cadenceOf(schedule) : null
    setTemplateId(schedule?.templateId ?? '')
    setCadenceKey(schedule ? (matched?.key ?? 'custom') : 'dailyYesterday')
    setRecurrence(schedule?.recurrence ?? 'DAILY')
    setPeriod(schedule?.period ?? 'RECURRENCE_PERIOD')
    setPeriodTime(schedule?.periodTime || '07:00')
    setDayOfWeek(schedule?.periodDayOfWeek ?? 'MONDAY')
    setDayOfMonth(schedule?.periodDayOfMonth ?? 1)
    setName(schedule?.name ?? '')
    setNameEdited(!!schedule)
    setUserIds(schedule?.recipientUsers.map((user) => user.id) ?? [])
    setEmails(schedule?.recipientEmailAddresses ?? [])
    setCurrency(schedule?.currency || currentNetwork?.defaultCurrency || '')
    setAdvanced(false)
  }, [open, schedule, currentNetwork?.defaultCurrency])

  // A new schedule starts on the first template that can serve this object.
  useEffect(() => {
    if (open && !templateId && templates.length > 0) setTemplateId(templates[0].id)
  }, [open, templates, templateId])

  // A cadence the chosen template cannot run — an end-of-flight report on a
  // template with no flight — falls back to the first one it can.
  useEffect(() => {
    if (cadenceKey === 'custom' || offered.length === 0) return
    if (!offered.some((item) => item.key === cadenceKey)) pickCadence(offered[0].key)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.reportType])

  const pickCadence = (key: CadenceKey) => {
    const picked = cadence(key)
    setCadenceKey(key)
    setRecurrence(picked.recurrence)
    setPeriod(picked.period)
    setPeriodTime(picked.periodTime ?? '')
    if (picked.periodDayOfWeek) setDayOfWeek(picked.periodDayOfWeek)
    if (picked.periodDayOfMonth) setDayOfMonth(picked.periodDayOfMonth)
  }

  const defaultName = useMemo(() => {
    const cadenceLabel =
      cadenceKey === 'custom' ? t(`reports.recurrence.${recurrence}`) : t(cadence(cadenceKey).labelKey)
    return [target.name, cadenceLabel].filter(Boolean).join(' — ')
  }, [target.name, cadenceKey, recurrence, t])

  useEffect(() => {
    if (!nameEdited) setName(defaultName)
  }, [defaultName, nameEdited])

  /** The sentence under the cadence: when it runs, and what it will cover. */
  const summary = t('reports.summarySentence', {
    when: t(`reports.when.${recurrence}`, {
      time: periodTime || '00:00',
      day: t(`reports.days.${dayOfWeek}`),
      dayOfMonth,
    }),
    covers: t(`reports.covers.${period}`, { recurrence: t(`reports.every.${recurrence}`) }),
    timeZone,
  })

  const mutation = useMutation({
    mutationFn: () =>
      ReportsApi.saveSchedule(schedule?.id ?? newReportId(), {
        name: name.trim(),
        reportTemplate: { id: templateId },
        recurrence,
        period,
        // The API takes these whatever the recurrence is, but a day means
        // nothing to a daily report — sending it back would only confuse the
        // next person to read the schedule.
        ...(periodTime && recurrence !== 'END_OF_FLIGHT' && recurrence !== 'ONCE'
          ? { periodTime }
          : {}),
        ...(recurrence === 'WEEKLY' ? { periodDayOfWeek: dayOfWeek } : {}),
        ...(recurrence === 'MONTHLY' ? { periodDayOfMonth: dayOfMonth } : {}),
        timeZone,
        currency: currency || undefined,
        locale: currentUser?.locale || undefined,
        parameters: { [REPORT_TARGETS[target.type].parameter]: target.id },
        recipientUsers: userIds.map((id) => ({ id })),
        recipientEmailAddresses: emails,
        objectState: 'ACTIVE',
      }),
    onSuccess: () => {
      toast.success(schedule ? t('reports.scheduleSaved') : t('reports.scheduleCreated'))
      queryClient.invalidateQueries({ queryKey: ['report-schedules', target.type, target.id] })
      onOpenChange(false)
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const userOptions = (users?.results ?? []).map((user) => ({
    label: user.displayName || user.name || user.username,
    value: user.id,
  }))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{schedule ? t('reports.editSchedule') : t('reports.newSchedule')}</DialogTitle>
          <DialogDescription>{t('reports.scheduleHelp')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="p-field">
            <FieldLabel>{t('reports.template')}</FieldLabel>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger>
                <SelectValue placeholder={t('reports.templatePlaceholder')}>
                  {template?.name ?? null}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {templates.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="p-field">
            <FieldLabel>{t('reports.cadenceLabel')}</FieldLabel>
            <div className="grid gap-2 sm:grid-cols-2">
              {offered.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => pickCadence(item.key)}
                  className={cn(
                    'flex cursor-pointer flex-col gap-0.5 rounded-(--ad-radius) border p-2.5 text-left transition-colors hover:bg-accent',
                    cadenceKey === item.key && 'border-(--ad-primary) bg-(--ad-primary-soft)'
                  )}
                >
                  <span className="flex items-center gap-1.5 text-xs font-semibold">
                    {t(item.labelKey)}
                    {cadenceKey === item.key && <Check className="size-3.5" />}
                  </span>
                  <span className="text-xs text-muted-foreground">{t(item.summaryKey)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Refinements that mean something for the chosen cadence. */}
          {(recurrence === 'DAILY' || recurrence === 'WEEKLY' || recurrence === 'MONTHLY') && (
            <div className="field-grid field-grid--3-responsive">
              {recurrence === 'WEEKLY' && (
                <div className="p-field">
                  <FieldLabel>{t('reports.dayOfWeek')}</FieldLabel>
                  <Select value={dayOfWeek} onValueChange={(v) => setDayOfWeek(v as WeekDay)}>
                    <SelectTrigger>
                      <SelectValue>{t(`reports.days.${dayOfWeek}`)}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {WEEK_DAYS.map((day) => (
                        <SelectItem key={day} value={day}>
                          {t(`reports.days.${day}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {recurrence === 'MONTHLY' && (
                <div className="p-field">
                  <FieldLabel hint={t('reports.dayOfMonthHint')}>{t('reports.dayOfMonth')}</FieldLabel>
                  <Input
                    type="number"
                    min={1}
                    max={MAX_DAY_OF_MONTH}
                    value={dayOfMonth}
                    onChange={(e) =>
                      setDayOfMonth(
                        Math.min(Math.max(Number(e.target.value) || 1, 1), MAX_DAY_OF_MONTH)
                      )
                    }
                  />
                </div>
              )}
              <div className="p-field">
                <FieldLabel>{t('reports.timeOfDay')}</FieldLabel>
                <Input
                  type="time"
                  value={periodTime}
                  onChange={(e) => setPeriodTime(e.target.value)}
                />
              </div>
            </div>
          )}

          <p className="flex items-start gap-2 rounded-(--ad-radius) bg-(--ad-neutral-soft) p-2.5 text-xs text-(--ad-text-muted)">
            <CalendarClock className="mt-px size-3.5 shrink-0" />
            {summary}
          </p>

          <div className="p-field">
            <FieldLabel>{t('reports.name')}</FieldLabel>
            <Input
              value={name}
              onChange={(e) => {
                setNameEdited(true)
                setName(e.target.value)
              }}
            />
          </div>

          <Separator />

          <div className="p-field">
            <FieldLabel tooltip={t('reports.recipientsHelp')}>{t('reports.recipients')}</FieldLabel>
            <MultiSelect
              options={userOptions}
              value={userIds}
              onChange={setUserIds}
              placeholder={t('reports.recipientsPlaceholder')}
            />
          </div>

          <div className="p-field">
            <FieldLabel tooltip={t('reports.emailRecipientsHelp')}>
              {t('reports.emailRecipients')}
            </FieldLabel>
            <TokenInput
              value={emails}
              onChange={setEmails}
              lowercase
              placeholder={t('reports.emailPlaceholder')}
            />
          </div>

          <AdvancedSection open={advanced} onOpenChange={setAdvanced}>
            <div className="field-grid field-grid--3-responsive">
              <div className="p-field">
                <FieldLabel tooltip={t('reports.recurrenceHelp')}>
                  {t('reports.recurrenceLabel')}
                </FieldLabel>
                <Select
                  value={recurrence}
                  onValueChange={(v) => {
                    setRecurrence(v as Recurrence)
                    setCadenceKey('custom')
                  }}
                >
                  <SelectTrigger>
                    <SelectValue>{t(`reports.recurrence.${recurrence}`)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {RECURRENCES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`reports.recurrence.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="p-field">
                <FieldLabel tooltip={t('reports.periodHelp')}>{t('reports.coverage')}</FieldLabel>
                <Select
                  value={period}
                  onValueChange={(v) => {
                    setPeriod(v as ReportPeriod)
                    setCadenceKey('custom')
                  }}
                >
                  <SelectTrigger>
                    <SelectValue>{t(`reports.period.${period}`)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {PERIODS.map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`reports.period.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="p-field">
                <FieldLabel hint={timeZone}>{t('reports.currency')}</FieldLabel>
                <CurrencySelect value={currency} onValueChange={setCurrency} />
              </div>
            </div>
          </AdvancedSection>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!templateId || !name.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {schedule ? t('common.save') : t('reports.createSchedule')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
